import React from 'react';
import { Link } from 'react-router-dom';
import GuideLayout, { InfoBox, SectionCTA } from '../../components/GuideLayout';

const s = {
  breadcrumb: { fontSize: 13, color: '#94a3b8', marginBottom: 20 },
  breadcrumbLink: { color: '#2563eb', textDecoration: 'none' },
  h1: { fontSize: 32, fontWeight: 800, color: '#0f172a', lineHeight: 1.25, marginBottom: 16, letterSpacing: '-0.4px' },
  lead: { fontSize: 17, color: '#475569', lineHeight: 1.7, marginBottom: 32, borderBottom: '1px solid #e2e8f0', paddingBottom: 32 },
  h2: { fontSize: 22, fontWeight: 700, color: '#0f172a', marginTop: 36, marginBottom: 14, letterSpacing: '-0.2px' },
  h3: { fontSize: 17, fontWeight: 600, color: '#1e293b', marginTop: 24, marginBottom: 10 },
  p: { fontSize: 15, color: '#374151', lineHeight: 1.75, marginBottom: 16 },
  ul: { paddingLeft: 20, marginBottom: 16 },
  li: { fontSize: 15, color: '#374151', lineHeight: 1.75, marginBottom: 6 },
  steps: { marginBottom: 32 },
  step: { display: 'flex', gap: 20, marginBottom: 8 },
  stepNum: {
    width: 32, height: 32, borderRadius: '50%',
    background: '#1e40af', color: '#fff',
    fontWeight: 700, fontSize: 14, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  stepConnector: { width: 2, background: '#e2e8f0', margin: '0 0 0 15px', height: 16 },
  stepBody: { paddingBottom: 20, flex: 1 },
  stepTitle: { fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 6 },
  stepDesc: { fontSize: 14, color: '#475569', lineHeight: 1.65 },
  procGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, marginBottom: 24 },
  procCard: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '16px 18px' },
  procTitle: { fontSize: 14, fontWeight: 700, color: '#1e293b', marginBottom: 6 },
  procDesc: { fontSize: 13, color: '#64748b', lineHeight: 1.5 },
  nav: { display: 'flex', justifyContent: 'space-between', marginTop: 48, paddingTop: 24, borderTop: '1px solid #e2e8f0' },
  prevLink: { fontSize: 14, color: '#2563eb', textDecoration: 'none', fontWeight: 600 },
  nextLink: { fontSize: 14, color: '#2563eb', textDecoration: 'none', fontWeight: 600 },
};

const steps = [
  {
    title: 'Contract notice published',
    desc: 'The contracting authority publishes a notice describing what they need, the estimated value, and the deadline to apply or submit. Above EU thresholds this must be published on the EU-wide platform.',
    timing: 'Day 0',
  },
  {
    title: 'Tender documents released',
    desc: 'The full specification, evaluation criteria, pricing schedule, and contract terms are made available — usually via a procurement portal. You register your interest to access them.',
    timing: 'Day 0–7',
  },
  {
    title: 'Clarification questions',
    desc: 'Bidders submit written questions; the authority publishes all questions and answers to all registered bidders. This is your chance to resolve ambiguities before investing in a full proposal.',
    timing: 'Weeks 1–3',
  },
  {
    title: 'Prepare and submit your bid',
    desc: 'You assemble your proposal — typically a technical response, a pricing schedule, and supporting evidence (accounts, certificates, case studies). Submissions are usually electronic via a portal.',
    timing: 'Weeks 2–6',
  },
  {
    title: 'Evaluation',
    desc: 'An evaluation panel scores each submission against the published criteria. For multi-stage processes (restricted procedure), shortlisting happens first, then full tender evaluation.',
    timing: 'Weeks 6–12',
  },
  {
    title: 'Award notification',
    desc: 'All bidders are notified of the outcome. Successful bidders receive a contract award notice. Unsuccessful bidders are entitled to a debrief explaining their scores.',
    timing: 'After evaluation',
  },
  {
    title: 'Standstill period',
    desc: 'For above-threshold contracts, EU law requires a minimum 10-day pause between notifying the award and signing the contract. This gives unsuccessful bidders the opportunity to challenge the decision.',
    timing: '10+ days',
  },
  {
    title: 'Contract signed and work begins',
    desc: 'Once the standstill period passes without challenge, the contract is formally executed. The start date, payment terms, and reporting requirements are all set out in the contract document.',
    timing: 'After standstill',
  },
];

const procedures = [
  {
    title: 'Open procedure',
    desc: 'Any supplier can submit a full bid in one stage. Most common for straightforward contracts. The most transparent approach.',
  },
  {
    title: 'Restricted procedure',
    desc: 'Buyers first shortlist candidates via a Pre-Qualification Questionnaire (PQQ), then invite only shortlisted companies to submit full tenders. Reduces evaluation burden for complex contracts.',
  },
  {
    title: 'Competitive dialogue',
    desc: 'Used for highly complex contracts where the buyer is not sure of the best solution. Structured dialogue between buyer and shortlisted bidders before final tenders are invited.',
  },
  {
    title: 'Negotiated procedure',
    desc: 'Allows direct negotiation with suppliers. Only permitted in specific circumstances — e.g. extreme urgency, or where only one supplier can do the work.',
  },
];

export default function BiddingProcess() {
  return (
    <GuideLayout>
      <div style={s.breadcrumb}>
        <Link to="/guide" style={s.breadcrumbLink}>Tender Guide</Link> &rsaquo; The bidding process
      </div>

      <h1 style={s.h1}>The bidding process, step by step</h1>
      <p style={s.lead}>
        From the moment a tender is published to the day a contract is signed, the process typically takes between six weeks and six months. Understanding each stage helps you decide when to engage, what to prepare, and where the real risks are.
      </p>

      <h2 style={s.h2}>The eight stages of a public tender</h2>
      <div style={s.steps}>
        {steps.map((step, i) => (
          <div key={i}>
            <div style={s.step}>
              <div style={s.stepNum}>{i + 1}</div>
              <div style={s.stepBody}>
                <div style={s.stepTitle}>{step.title} <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 400, marginLeft: 8 }}>{step.timing}</span></div>
                <div style={s.stepDesc}>{step.desc}</div>
              </div>
            </div>
            {i < steps.length - 1 && <div style={s.stepConnector} />}
          </div>
        ))}
      </div>

      <InfoBox>
        <strong>Typical timelines:</strong> An open procedure for a services contract usually runs 4&ndash;8 weeks from publication to submission deadline. Complex IT or infrastructure projects can have 3&ndash;6 month timelines. Always check the specific dates in the contract notice — they override any general guidance.
      </InfoBox>

      <h2 style={s.h2}>Types of procurement procedure</h2>
      <p style={s.p}>The type of procedure determines how many stages there are and who can submit a bid:</p>
      <div style={s.procGrid}>
        {procedures.map((p, i) => (
          <div key={i} style={s.procCard}>
            <div style={s.procTitle}>{p.title}</div>
            <div style={s.procDesc}>{p.desc}</div>
          </div>
        ))}
      </div>

      <h2 style={s.h2}>What you typically need to submit</h2>
      <p style={s.p}>Most tender responses consist of three parts:</p>

      <h3 style={s.h3}>1. Technical proposal (or "quality response")</h3>
      <p style={s.p}>
        This is where you explain how you will deliver the contract. It should directly address each evaluation criterion in the specification — usually covering your methodology, your team, your relevant experience (with case studies), and your approach to risk and quality. This is the part that separates winning bids from also-rans.
      </p>

      <h3 style={s.h3}>2. Pricing schedule</h3>
      <p style={s.p}>
        Most tenders provide a fixed pricing template — day rates, unit costs, or a total fixed price. Buyers are not always looking for the cheapest submission; price is typically weighted alongside quality. Submitting unrealistically low prices can raise credibility concerns.
      </p>

      <h3 style={s.h3}>3. Supporting evidence</h3>
      <p style={s.p}>
        Financial accounts (usually last two years), professional liability insurance certificate, relevant accreditations (ISO certifications, sector registrations), and a signed self-declaration (ESPD) confirming you meet the exclusion criteria.
      </p>

      <h2 style={s.h2}>The debrief — and why it matters</h2>
      <p style={s.p}>
        Unsuccessful bidders have a right to a debrief. This is a feedback session (usually written, sometimes by call) where the buyer explains your scores against each criterion and compares them to the winning submission. Even if you lose, a debrief tells you exactly what to improve for next time — and whether the scoring was fair. Always request one.
      </p>

      <SectionCTA />

      <div style={s.nav}>
        <Link to="/guide/who-can-bid" style={s.prevLink}>&larr; Who can bid?</Link>
        <Link to="/guide/winning-a-bid" style={s.nextLink}>Next: What makes a winning bid? &rarr;</Link>
      </div>
    </GuideLayout>
  );
}
